import FormData from "csi/action/FormData";
import JsonResponse from "csi/comm/JsonResponse";
import PostRequest from "csi/comm/PostRequest";
import csi from "csi/main";
import message from "csi/message";
import { SlimCardStore } from "csi/middleware/card/SlimCardStore";
import { SlimCardWithStatus } from "csi/middleware/card/SlimCardWithStatus";
import { ControllerSaveFunction } from "csi/middleware/common";
import { getSearch, runSearch } from "csi/service/dataStoreService";
import { resolveNodesWithDependencies } from "csi/service/nodeService";
import { submitWriter } from "csi/service/writerService";
import { C8Node, ICache, ParameterMap, cnl } from "csi/typings";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { CheckboxTreeNode } from "designSystem/components/checkbox/CheckboxTree";
import { ILocaleProvider } from "designSystem/locale";
import { grey } from "designSystem/styles/color";
import { PartMaterial } from "src/Apparel/Bom/MassUpdate/MassUpdateBOMsWizardStore";
import { RestoreFromBOMResources, RestoreFromBOMStore } from "./RestoreFromBOMStore";

const BLANK_SECTION: string = "Blanks";

const MODULE: string = "ProductSpec";

const RESTORE_FROM_STYLE_BOM_ACTION: string = "RestoreFromStyleBOM";

const RESTORE_FROM_THEME_BOM_ACTION: string = "RestoreFromThemeBOM";

const BOM_SECTION_DEFINITION: string = "BOMSectionDefinition";

/** create BOMDSLineItem tree */
export const createApparelBOMDSLineItemTree = async (dsLineItems: C8Node[], statusLabelRes: string, locale: ILocaleProvider, cache: ICache): Promise<Map<string, CheckboxTreeNode>> => {
	const sectionsMap = new Map<string, CheckboxTreeNode>();
	sectionsMap.set(csi.EMPTY_URL, new CheckboxTreeNode(undefined, csi.EMPTY_URL, BLANK_SECTION, true));
	dsLineItems.forEach((node) => {
		if (node.$Type === BOM_SECTION_DEFINITION) {
			const sName = locale.getNodeName(node);
			sectionsMap.set(node.$URL, new CheckboxTreeNode(undefined, node.$URL, sName, true));
		}
	});

	dsLineItems
		.filter((node) => node.$Type === "PartMaterial")
		.map((p) => {
			const pName = locale.getNodeName(p);
			const partMaterial = new PartMaterial(undefined, p.$URL, pName, sectionsMap.get(p.DSSection).uniqueId, p.DSSection, true);
			sectionsMap.get(partMaterial.sectionUrl)
				.addChild(partMaterial);
			partMaterial.label = SlimCardWithStatus({
				displayImage: true,
				slimCardStore: new SlimCardStore(p.$URL, cache),
				statusLabelRes,
			});
			return partMaterial;
		});

	//REMOVE empty sections
	const emptySectionsKey = [];
	sectionsMap.forEach((section) => {
		if (!section.hasChildren) {
			emptySectionsKey.push(section.uniqueId);
		}
	});
	if (emptySectionsKey.length > 0) {
		emptySectionsKey.forEach((key) => {
			sectionsMap.delete(key);
		});
	}
	return sectionsMap;
};

/** create delete BOMDSLineItem tree */
export const createDeleteItemsTree = (dsLineItems: C8Node[], statusLabelRes: string, locale: ILocaleProvider, cache: ICache): CheckboxTreeNode => {
	const deletePlacements = new CheckboxTreeNode(undefined, "Delete", locale.resource("Delete"), true);
	dsLineItems.forEach((node) => {
		if (node.$Type !== BOM_SECTION_DEFINITION) {
			const sName = locale.getNodeName(node);
			const checkBoxNode = new CheckboxTreeNode(undefined, node.$URL, sName, true);
			checkBoxNode.label = SlimCardWithStatus({
				displayImage: true,
				slimCardStore: new SlimCardStore(node.$URL, cache),
				statusLabelRes,
				textColor: grey[800],
			});
			deletePlacements.addChild(checkBoxNode);
		}
	});

	return deletePlacements;
};

/** fetch BOMDSLineItems to update */
export const fetchBOMDSLineItemsToUpdate = async (bomRevisionURL: cnl, actionId: string, cache: ICache): Promise<C8Node[]> => {
	let searchID = "DSLineItem.ApparelBOMRevision.StyleBOMDSLineItem";
	let searchParams: ParameterMap = { isLinkedToTheme: true };
	if (actionId === RESTORE_FROM_STYLE_BOM_ACTION) {
		const sourceBOMUrl = cache.get(bomRevisionURL).__Parent__;
		const hostBOMUrl = cache.get(sourceBOMUrl).LinkedHostDataSheet;
		const hostBOM = (await resolveNodesWithDependencies([hostBOMUrl], ["Child:CurrentRevision/Child:Items"], cache)).at(0);
		const hostBOMRevisionUrl =  hostBOM.CurrentRevision;
		const items = cache.get(hostBOMRevisionUrl).Items;
		const linkedDSLineItemSysIDs = cache.getNodes(items).map((item) => item.LinkedDSLineItemSysID);
		searchID = "DSLineItem.ApparelBOMRevision.SourceBOMDSLineItem";
		searchParams = { linkedDSLineItemSysIDsList: linkedDSLineItemSysIDs };
	}
	const search = await getSearch(searchID, bomRevisionURL, searchParams).byType("PartMaterial");
	const response = await runSearch(search, cache);
	return response.getNodes();
};

/** fetch BOMDSLineItems */
const fetchBOMDSLineItems = async (bomRevisionURL: cnl, actionId: string, cache: ICache): Promise<C8Node[]> => {
	const bomDSLineItem = actionId === RESTORE_FROM_STYLE_BOM_ACTION ? "DSLineItem.ApparelBOMRevision.SourceBOMDSLineItem" : "DSLineItem.ApparelBOMRevision.StyleBOMDSLineItem";
	const search = await getSearch(bomDSLineItem, bomRevisionURL).byType("PartMaterial");
	const response = await runSearch(search, cache);
	return response.getNodes();
};

/** get items to be added to Style BOM */
const getItemsToBeAddedToStyleBOM = (styleBomItems: C8Node[], linkedThemeBomDSLineItems: C8Node[]): C8Node[] => {
	const styleBOMitemsMap = new Map<string, C8Node>();
	styleBomItems.forEach((styleBOMitem) => {
		styleBOMitemsMap.set(styleBOMitem.ThemeBOMSysID, styleBOMitem);
	});
	const newItemsToBeAdded = linkedThemeBomDSLineItems.filter((linkedThemeBomDSLineItem) => {
		return !styleBOMitemsMap.has(linkedThemeBomDSLineItem.SysID) || linkedThemeBomDSLineItem.$Type === BOM_SECTION_DEFINITION;
	});

	return newItemsToBeAdded;
};

/** get items to be added to Source BOM */
const getItemsToBeAddedToSourceBOM = (sourceBOMitems: C8Node[], linkedStyleBomDSLineItems: C8Node[]): C8Node[] => {
	const sourceBOMitemsMap = new Map<string, C8Node>();
	sourceBOMitems.forEach((sourceBOMitem) => {
		sourceBOMitemsMap.set(sourceBOMitem.LinkedDSLineItemSysID, sourceBOMitem);
	});
	const newItemsToBeAdded = linkedStyleBomDSLineItems.filter((linkedStyleBomDSLineItem) => {
		return !sourceBOMitemsMap.has(linkedStyleBomDSLineItem.LinkedDSLineItemSysID) || linkedStyleBomDSLineItem.$Type === BOM_SECTION_DEFINITION;
	});

	return newItemsToBeAdded;
};

/** get items to be deleted on Style BOM */
const getItemsToBeDeletedToStyleBOM = (styleBOMitems: C8Node[]): C8Node[] => {
	const itemsToBeDeleted = styleBOMitems.filter((styleBOMitem) => {
		return styleBOMitem.FormerThemeSysID !== "";
	});
	return itemsToBeDeleted;
};

/** get items to be deleted on Source BOM */
const getItemsToBeDeletedOnSourceBom = async (bomRevisionURL: cnl, cache: ICache): Promise<C8Node[]> => {
	const request = new PostRequest(MODULE, "GetItemsToBeDeletedOnSourceBom")
		.addParameter("URL", bomRevisionURL);
	try {
		const response = await request.go();
		return response.getResultNodes();
	} catch (error) {
		message.error(error.message);
		return null;
	}
};

/** Create and initialize RestoreFromThemeStore */
export const createRestoreFromThemeStore = async (
	actionId: string, 
	cache: ICache, 
	formData: FormData, 
	contextNode: C8Node, 
	onClose: () => void, 
	onSave: ControllerSaveFunction, 
	locale: ILocaleProvider,
	resources: RestoreFromBOMResources,
): Promise<RestoreFromBOMStore> => {
	const bomRevisionURL = contextNode.$URL;
	const styleLinkedItems = await fetchBOMDSLineItemsToUpdate(bomRevisionURL, actionId, cache);
	const updateItemsMap = await createApparelBOMDSLineItemTree(styleLinkedItems, "RestoreFromThemeBOM_StatusUpdate", locale, cache);
	const styleBOMitems = await fetchBOMDSLineItems(bomRevisionURL, actionId, cache);
	const linkedStyleBomDSLineItems = await getValidLinkedBOMDSLineItems(bomRevisionURL, cache, actionId);
	const addItems = getItemsToBeAddedToStyleBOM(styleBOMitems, linkedStyleBomDSLineItems);
	const addItemsMap = await createApparelBOMDSLineItemTree(addItems, "RestoreFromThemeBOM_StatusAdd", locale, cache);
	const deleteItems = getItemsToBeDeletedToStyleBOM(styleBOMitems);
	const deleteItemsMap = createDeleteItemsTree(deleteItems, "RestoreFromThemeBOM_StatusDelete", locale, cache);
	const store = new RestoreFromBOMStore(actionId, cache, formData, contextNode, onClose, onSave, locale, resources, addItemsMap, deleteItemsMap, updateItemsMap, false, false);
	return store;
};

/** Create and initialize RestoreFromStyleStore */
export const createRestoreFromStyleStore = async (
	actionId: string, 
	cache: ICache, 
	formData: FormData, 
	contextNode: C8Node, 
	onClose: () => void, 
	onSave: ControllerSaveFunction, 
	locale: ILocaleProvider,
	resources: RestoreFromBOMResources,
): Promise<RestoreFromBOMStore> => {
	const bomRevisionURL = contextNode.$URL;
	const sourceLinkedItems = await fetchBOMDSLineItemsToUpdate(bomRevisionURL, actionId, cache);
	const updateItemsMap = await createApparelBOMDSLineItemTree(sourceLinkedItems, "RestoreFromStyleBOM_StatusUpdate", locale, cache);
	const sourceBOMitems = await fetchBOMDSLineItems(bomRevisionURL, actionId, cache);
	const linkedSourceBomDSLineItems = await getValidLinkedBOMDSLineItems(bomRevisionURL, cache, actionId);
	const addItems = getItemsToBeAddedToSourceBOM(sourceBOMitems, linkedSourceBomDSLineItems);
	const addItemsMap = await createApparelBOMDSLineItemTree(addItems, "RestoreFromStyleBOM_StatusAdd", locale, cache);
	const deleteItems = await getItemsToBeDeletedOnSourceBom(bomRevisionURL, cache);
	const deleteItemsMap = createDeleteItemsTree(deleteItems, "RestoreFromStyleBOM_StatusDelete", locale, cache);
	const store = new RestoreFromBOMStore(actionId, cache, formData, contextNode, onClose, onSave, locale, resources, addItemsMap, deleteItemsMap, updateItemsMap, false, true);
	return store;
};

/** get DsLineItems collection filtering invalid placement checking by the productType and season availability*/
export async function getValidLinkedBOMDSLineItems(bomRevisionURL: cnl, cache: ICache, actionId: string): Promise< C8Node[] | null> {
	const operation = actionId === RESTORE_FROM_STYLE_BOM_ACTION ? "GetValidLinkedSourceBOMDSLineItems" : "GetValidLinkedStyleBOMDSLineItems";
	const request = new PostRequest(MODULE, operation)
		.addParameter("URL", bomRevisionURL);
	try {
		const response = await request.go();
		cache.cacheResponse(response);
		return response.getResultNodes();
	} catch (error) {
		message.error(error.message);
		return null;
	}
}


/** Restore from BOM */
export async function restoreFromBOM(actionId: string, restoreBOMRevisionURL: cnl, restoreAllPlacements: boolean, overrideAttributes: boolean, addPlacements: CheckboxTreeNode[], updatePlacements: CheckboxTreeNode[], deletePlacements: CheckboxTreeNode): Promise<JsonResponse> {
	const NotSelectedDeletedPlacements = deletePlacements.allLeafNodes.filter((node) => !node.state).
		map((node) => node.uniqueId);	
	const operation = actionId;
	const writer = new Writer();
	const call = writer.callMethod(MODULE, operation)
		.addParameter("URL", xml.ATTR_REF, restoreBOMRevisionURL)
		.addParameter("AddPlacements", xml.ATTR_REFLIST, restoreAllPlacements ? addPlacements.map((node) => node.uniqueId) : [])
		.addParameter("UpdatePlacements", xml.ATTR_REFLIST, updatePlacements.map((node) => node.uniqueId))
		.addParameter("DeletePlacements", xml.ATTR_REFLIST, deletePlacements.selectedNodes.map((node) => node.uniqueId))
		.addParameter("NotSelectedDeletedPlacements", xml.ATTR_REFLIST, NotSelectedDeletedPlacements)
		.addParameter("RestoreAllPlacements", xml.ATTR_BOOLEAN, restoreAllPlacements);
	
	if (actionId === RESTORE_FROM_STYLE_BOM_ACTION) {
		call.addParameter("OverrideAttributes", xml.ATTR_BOOLEAN, overrideAttributes);
	}

	return submitWriter(writer);
}